import { useEffect, useRef } from 'react';
import type { UserData } from 'types/user';
import { logError } from 'utils/monitoring';
import { privilegesVersionTracker, type PrivilegesVersionTracker } from 'utils/privilegesVersion';

type PrivilegesSyncTarget = {
    isLoggedIn: boolean;
    user: Pick<UserData, 'id' | 'authUserId'> | null;
    applySessionUser: (userData: UserData) => void;
};

// The session cookie is authoritative for what the user may do now, so the permissions are
// re-read from it rather than derived from the version value itself.
async function syncUserFromSession(
    getTarget: () => PrivilegesSyncTarget,
    isStillLatest: () => boolean,
    tracker: PrivilegesVersionTracker
): Promise<void> {
    if (!getTarget().isLoggedIn) return;
    try {
        const response = await fetch(import.meta.env.VITE_API_URL + '/v1/users/current', {
            credentials: 'include',
        });
        if (!response.ok) {
            // A lapsed session surfaces through the existing 401 handling on the tab's next API
            // call; anything else is worth another attempt on the next response.
            if (response.status !== 401) tracker.markUnapplied();
            return;
        }
        const sessionUser = (await response.json()) as UserData;
        // A slow fetch for an earlier version must not overwrite a newer one
        if (!isStillLatest()) return;
        const { user, applySessionUser } = getTarget();
        // A response for a previous identity must not replace the one the session switched to
        // meanwhile; identity changes belong to the impersonation sync.
        if (!user || sessionUser.id !== user.id || sessionUser.authUserId !== user.authUserId) return;
        applySessionUser(sessionUser);
    } catch (error) {
        tracker.markUnapplied();
        logError(error);
    }
}

export function usePrivilegesSync(
    target: PrivilegesSyncTarget,
    tracker: PrivilegesVersionTracker = privilegesVersionTracker
): void {
    // Latest target for the mount-once subscription (a stable subscription can't close over current state)
    const targetRef = useRef(target);
    useEffect(() => {
        targetRef.current = target;
    });

    useEffect(() => {
        let latestRequestId = 0;
        return tracker.subscribe(() => {
            const requestId = ++latestRequestId;
            void syncUserFromSession(
                () => targetRef.current,
                () => requestId === latestRequestId,
                tracker
            );
        });
    }, [tracker]);
}
